import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

interface NoteCard {
  id: string;
  number: string;
  title: string;
  description: string;
  link: string;
  rotation: number;
}

interface DarkNotesSectionProps {
  notes: NoteCard[];
}

export const DarkNotesSection: React.FC<DarkNotesSectionProps> = ({ notes }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  
  // Refs for pin elements
  const pinRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Disconnect after first trigger to prevent re-animations
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Generate abstract preview pattern for each note
  const generateAbstractPreview = (index: number) => {
    const patterns = [
      // Pattern 1: Diagonal lines with beige accent
      <svg width="100%" height="100%" viewBox="0 0 200 120" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#13100B', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#1a1612', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#B08A5A', stopOpacity: 0.3 }} />
          </linearGradient>
        </defs>
        <rect width="200" height="120" fill="url(#grad1)" />
        <path d="M0,40 L200,0 L200,20 L0,60 Z" fill="#B08A5A" opacity="0.15" />
        <path d="M0,80 L200,40 L200,60 L0,100 Z" fill="#B08A5A" opacity="0.1" />
        <circle cx="160" cy="90" r="30" fill="#B08A5A" opacity="0.08" />
      </svg>,
      
      // Pattern 2: Geometric shapes
      <svg width="100%" height="100%" viewBox="0 0 200 120" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="grad2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#13100B', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#1a1612', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <rect width="200" height="120" fill="url(#grad2)" />
        <rect x="30" y="20" width="60" height="60" fill="none" stroke="#B08A5A" strokeWidth="2" opacity="0.2" />
        <circle cx="150" cy="50" r="40" fill="none" stroke="#B08A5A" strokeWidth="2" opacity="0.15" />
        <path d="M10,100 L50,60 L90,100 Z" fill="#B08A5A" opacity="0.1" />
      </svg>,
      
      // Pattern 3: Soft gradient with dots
      <svg width="100%" height="100%" viewBox="0 0 200 120" preserveAspectRatio="xMidYMid slice">
        <defs>
          <radialGradient id="grad3">
            <stop offset="0%" style={{ stopColor: '#B08A5A', stopOpacity: 0.2 }} />
            <stop offset="100%" style={{ stopColor: '#13100B', stopOpacity: 1 }} />
          </radialGradient>
          <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.5" fill="#B08A5A" opacity="0.15" />
          </pattern>
        </defs>
        <rect width="200" height="120" fill="url(#grad3)" />
        <rect width="200" height="120" fill="url(#dots)" />
        <ellipse cx="100" cy="60" rx="80" ry="50" fill="#B08A5A" opacity="0.08" />
      </svg>
    ];
    
    return patterns[index % patterns.length];
  };
  
  // Calculate dynamic line paths between pins
  const [linePaths, setLinePaths] = useState({ line1: '', line2: '' });
  
  useEffect(() => {
    const updateLines = () => {
      if (!containerRef.current || pinRefs.current.some(pin => !pin)) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      
      const getPinCenter = (index: number) => {
        const pin = pinRefs.current[index];
        if (!pin) return { x: 0, y: 0 };
        
        const pinRect = pin.getBoundingClientRect();
        return {
          x: pinRect.left + pinRect.width / 2 - containerRect.left,
          y: pinRect.top + pinRect.height / 2 - containerRect.top
        };
      };
      
      const pin1 = getPinCenter(0);
      const pin2 = getPinCenter(1);
      const pin3 = getPinCenter(2);
      
      // Line 01 -> 02
      const line1 = `M ${pin1.x} ${pin1.y} L ${pin2.x} ${pin2.y}`;
      // Line 02 -> 03
      const line2 = `M ${pin2.x} ${pin2.y} L ${pin3.x} ${pin3.y}`;
      
      setLinePaths({ line1, line2 });
    };
    
    updateLines();
    window.addEventListener('resize', updateLines);
    
    // Update lines periodically
    const interval = setInterval(updateLines, 50);
    
    return () => {
      window.removeEventListener('resize', updateLines);
      clearInterval(interval);
    };
  }, [isVisible]);

  // Levitation animation classes - unique for each card
  const levitationClasses = [
    'levitate-card-1',
    'levitate-card-2',
    'levitate-card-3'
  ];

  return (
    <>
      {/* Inject levitation CSS animations */}
      <style>{`
        @keyframes levitate-1 {
          0%, 100% {
            transform: translate3d(0, 0, 0) rotate(0deg);
          }
          25% {
            transform: translate3d(4px, -10px, 0) rotate(0.5deg);
          }
          50% {
            transform: translate3d(-3px, -14px, 0) rotate(-0.4deg);
          }
          75% {
            transform: translate3d(5px, -8px, 0) rotate(0.6deg);
          }
        }
        
        @keyframes levitate-2 {
          0%, 100% {
            transform: translate3d(0, 0, 0) rotate(0deg);
          }
          30% {
            transform: translate3d(-5px, -12px, 0) rotate(-0.6deg);
          }
          60% {
            transform: translate3d(3px, -9px, 0) rotate(0.4deg);
          }
          85% {
            transform: translate3d(-4px, -13px, 0) rotate(-0.5deg);
          }
        }
        
        @keyframes levitate-3 {
          0%, 100% {
            transform: translate3d(0, 0, 0) rotate(0deg);
          }
          20% {
            transform: translate3d(3px, -11px, 0) rotate(0.4deg);
          }
          55% {
            transform: translate3d(-4px, -13px, 0) rotate(-0.7deg);
          }
          80% {
            transform: translate3d(6px, -9px, 0) rotate(0.5deg);
          }
        }
        
        .levitate-card-1 {
          animation: levitate-1 6.5s ease-in-out infinite;
          will-change: transform;
        }
        
        .levitate-card-2 {
          animation: levitate-2 7.2s ease-in-out infinite 0.5s;
          will-change: transform;
        }
        
        .levitate-card-3 {
          animation: levitate-3 8s ease-in-out infinite 1s;
          will-change: transform;
        }
        
        .levitate-card-1:hover,
        .levitate-card-2:hover,
        .levitate-card-3:hover {
          animation-play-state: paused;
        }
      `}</style>
    
      <div ref={sectionRef} className="relative py-16 lg:py-24">
        {/* Desktop Layout */}
        <div className="hidden lg:block container-custom relative" ref={containerRef}>
          {/* Dynamic dashed connecting lines - SVG */}
          <svg 
            className="absolute inset-0 w-full h-full pointer-events-none" 
            style={{ zIndex: 5 }}
          >
            {/* Line from Note 01 to Note 02 */}
            <path
              d={linePaths.line1}
              stroke="#B08A5A"
              strokeWidth="2"
              strokeDasharray="8,8"
              fill="none"
              opacity={isVisible ? "0.3" : "0"}
              style={{ transition: 'opacity 0.3s ease' }}
            />
            
            {/* Line from Note 02 to Note 03 */}
            <path
              d={linePaths.line2}
              stroke="#B08A5A"
              strokeWidth="2"
              strokeDasharray="8,8"
              fill="none"
              opacity={isVisible ? "0.3" : "0"}
              style={{ transition: 'opacity 0.3s ease' }}
            />
          </svg>

          {/* Notes positioned in custom layout */}
          <div className="relative" style={{ minHeight: '800px' }}>
            {notes.map((note, index) => {
              const positions = [
                { top: '5%', left: '8%' },      // Note 01 - top left
                { top: '15%', right: '8%' },    // Note 02 - top right
                { bottom: '5%', left: 'calc(35% - 80px)', transform: 'translateY(80px)' }   // Note 03 - shifted left and down by 1 grid cell
              ];
              
              const position = positions[index] || positions[0];
              
              return (
                <motion.div
                  key={note.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ 
                    duration: 0.3, 
                    delay: 0,
                    ease: "easeOut"
                  }}
                  style={{
                    position: 'absolute',
                    ...position,
                    width: '340px',
                    zIndex: 10 + index
                  }}
                >
                  <Link to={note.link} className="block">
                    <div 
                      className={`relative cursor-pointer ${levitationClasses[index]}`}
                      style={{
                        transformOrigin: 'center center'
                      }}
                    >
                      <div 
                        className="relative bg-[#1a1612] rounded-[16px] p-6 shadow-[0_8px_30px_rgba(0,0,0,0.4)] border border-white/5 transition-all duration-300 hover:shadow-[0_12px_40px_rgba(176,138,90,0.2)] hover:border-primary/20"
                        style={{
                          transform: `rotate(${note.rotation}deg)`,
                        }}
                      >
                        {/* Hole at top center (Pin) */}
                        <div 
                          ref={(el) => (pinRefs.current[index] = el)}
                          className="absolute -top-3 left-1/2 -translate-x-1/2 z-20"
                        >
                          <div className="relative w-6 h-6">
                            {/* Outer ring - darker */}
                            <div className="absolute inset-0 rounded-full bg-[#0d0b08] shadow-inner" />
                            {/* Inner ring - lighter with highlight */}
                            <div className="absolute inset-[3px] rounded-full bg-[#13100B] border border-white/10" />
                            {/* Highlight */}
                            <div className="absolute top-1 left-1 w-2 h-2 rounded-full bg-white/5" />
                          </div>
                        </div>

                        {/* Number badge */}
                        <div className="absolute top-4 left-4 text-primary/50 font-mono text-sm font-bold">
                          {note.number}
                        </div>

                        {/* Abstract preview */}
                        <div className="mt-6 mb-4 rounded-xl overflow-hidden h-32 bg-[#13100B]">
                          {generateAbstractPreview(index)}
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-black uppercase tracking-tight text-white mb-2 group-hover:text-primary transition-colors duration-300">
                          {note.title}
                        </h3>

                        {/* Description */}
                        <p className="text-white/40 text-xs font-medium leading-relaxed line-clamp-3">
                          {note.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Mobile Layout - Vertical Stack */}
        <div className="lg:hidden container-custom space-y-8">
          {notes.map((note, index) => (
            <motion.div
              key={note.id}
              initial={{ opacity: 0, y: 12 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.3, 
                delay: 0,
                ease: "easeOut"
              }}
            >
              <Link to={note.link} className="block group">
                <div 
                  className="relative bg-[#1a1612] rounded-[16px] p-6 shadow-[0_8px_30px_rgba(0,0,0,0.4)] border border-white/5 transition-all duration-300 active:scale-[0.98]"
                  style={{
                    transform: `rotate(${note.rotation * 0.5}deg)`, // Reduced rotation on mobile
                  }}
                >
                  {/* Hole at top center */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                    <div className="relative w-6 h-6">
                      <div className="absolute inset-0 rounded-full bg-[#0d0b08] shadow-inner" />
                      <div className="absolute inset-[3px] rounded-full bg-[#13100B] border border-white/10" />
                      <div className="absolute top-1 left-1 w-2 h-2 rounded-full bg-white/5" />
                    </div>
                  </div>

                  {/* Number badge */}
                  <div className="absolute top-4 left-4 text-primary/50 font-mono text-sm font-bold">
                    {note.number}
                  </div>

                  {/* Abstract preview */}
                  <div className="mt-6 mb-4 rounded-xl overflow-hidden h-32 bg-[#13100B]">
                    {generateAbstractPreview(index)}
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-black uppercase tracking-tight text-white mb-2">
                    {note.title}
                  </h3>

                  {/* Description */}
                  <p className="text-white/40 text-xs font-medium leading-relaxed line-clamp-3">
                    {note.description}
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}

          {/* Vertical dashed line between cards on mobile */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px pointer-events-none hidden">
            <svg width="2" height="100%" className="absolute left-0 top-0">
              <line 
                x1="1" y1="0" 
                x2="1" y2="100%" 
                stroke="#B08A5A" 
                strokeWidth="2" 
                strokeDasharray="8,8" 
                opacity="0.2"
              />
            </svg>
          </div>
        </div>
      </div>
    </>
  );
};
